import {Component, OnInit, Input} from '@angular/core';
import {ReportService} from '../../services/report/report.service';
import {DiagramService} from '../../services/diagram/diagram.service';
import {FindingService} from '../../services/finding/finding.service';
import {Router, ActivatedRoute} from '@angular/router';
import Swal from 'sweetalert2';
import * as LC from 'literallycanvas';
import {ScaleControlStyle} from '@agm/core/services/google-maps-types';
import {environment} from '../../../../../environments/environment';
import { Location } from '@angular/common';
import { WdoService } from '../../../../services/wdo.service';

@Component({
  selector: 'app-diagram',
  templateUrl: './diagram.component.html',
  styleUrls: ['./diagram.component.scss']
})
export class DiagramComponent implements OnInit {
  page_title = 'Inspection: Diagram';
  private apiSite = environment.apiSite;
  lc: any;
  job_id: number;
  letterPrefix = '';
  /**
   * The quick-insert number buttons on the Text tool. Starts at 1-5, which is also what
   * WdoService falls back to if the mode cannot be read, and becomes 1-11 once the
   * deployment's mode is known to be 1-11 (the Type number then means location).
   */
  typeNumbers: number[] = [1, 2, 3, 4, 5];
  reportData = [];
  findingsData = [];
  uploadImage = {};
  textToolState = {
    fontSize: 16,
    bold: false,
    italic: false,
    fontFamily: 'sans-serif'
  };

  external = false;

  wideStairs = false;
  stairsStepInterval = 20;

  activeColor = 'green';
  baseColor = '#ccc';
  overlayColor = 'rgba(255,255,255,0.5)';
  dragging = false;
  loaded = false;
  imageLoaded = false;
  imageSrc = '';
  saveSuccess = false;
  typeSave = "p";
  lastSavedTime: string = '';
  lastSavedTimestamp: number = 0;

  constructor(
    private _router: Router,
    private router: ActivatedRoute,
    private reportService: ReportService,
    private diagramService: DiagramService,
    private findingService: FindingService,
    private location : Location,
    private wdoService: WdoService
  ) {
  }

  ngOnInit() {
    localStorage.setItem('tab', JSON.stringify(true));
    this.router.params.subscribe(params => {
      if (params.jobId) {
        this.job_id = params.jobId;
        this.getReport(this.job_id);
      }

    });

  }

  getReport(jobId) {
    this.reportService.getReportUsingJobId(jobId).subscribe(
      data => {
        this.reportData = data;
        // console.log(this.reportData);
        // Initialize lastSavedTime from database if available
        if (data && data['report_diagram_last_saved_at']) {
          this.formatAndSetLastSavedTime(data['report_diagram_last_saved_at']);
        }
        this.loadLc(this.reportData);
        this.getFindings(this.job_id);
      }
    );
  }

  getFindings(jobId) {
    this.findingService.getJobFindings(jobId).subscribe(
      data => {
        this.findingsData = data;
        // The toolbar is built once, so wait for the mode before building it. getConfig()
        // always emits - on failure it falls back to 1-5 - so the toolbar always appears.
        this.wdoService.getConfig().subscribe(cfg => {
          this.typeNumbers = this.typeNumbersFor(cfg);
          this.loadCustomText();
        });
      }
    );
  }

  loadLc(reportData) {
    const componentRef = this;

    class RectangleWithXTool extends LC.tools.Tool {
      name: string;
      iconName: string;
      optionsStyle: null;
      startX: number | null = null;
      startY: number | null = null;
      shape: any = null;

      constructor(lc: any) {
        super(lc);
        this.name = 'RectangleWithX';
        this.iconName = 'rectangle-x'; // Use a custom icon if needed
        this.optionsStyle = null;
      }

      begin(x: number, y: number, lc: any) {
        this.startX = x;
        this.startY = y;

        // Create a rectangle shape
        this.shape = LC.createShape('Rectangle', {
          x: x,
          y: y,
          width: 0,
          height: 0,
          strokeWidth: lc.opts.defaultStrokeWidth,
          strokeColor: lc.getColor('primary'),
          fillColor: lc.getColor('secondary'),
          isClosed: true,
        });
      }

      continue(x: number, y: number, lc: any) {
        if (this.startX === null || this.startY === null) return;

        const width = x - this.startX;
        const height = y - this.startY;

        // Update the rectangle dimensions
        this.shape.width = width;
        this.shape.height = height;

        // Clear the canvas and redraw all shapes
        lc.repaintLayer('main'); // Repaint all saved shapes
        lc.ctx.save(); // Save canvas state

        // Draw only the rectangle dynamically
        lc.drawShapeInProgress(this.shape);

        lc.ctx.restore(); // Restore canvas state
      }

      end(x: number, y: number, lc: any) {
        if (this.startX === null || this.startY === null || !this.shape) return;

        const width = x - this.startX;
        const height = y - this.startY;

        // Save the rectangle
        lc.saveShape(this.shape, true);

        // Create and save the diagonal lines
        const diagonalLines = this.createDiagonalLines(
          this.startX,
          this.startY,
          this.startX + width,
          this.startY + height,
          lc.opts.defaultStrokeWidth,
          lc.getColor('primary')
        );

        diagonalLines.forEach((line) => lc.saveShape(line, true));

        // Reset tool state
        this.startX = null;
        this.startY = null;
        this.shape = null;
      }

      createDiagonalLines(x1: number, y1: number, x2: number, y2: number, strokeWidth: number, color: string) {
        return [
          LC.createShape('Line', {
            x1: x1,
            y1: y1,
            x2: x2,
            y2: y2,
            strokeWidth,
            color,
          }),
          LC.createShape('Line', {
            x1: x2,
            y1: y1,
            x2: x1,
            y2: y2,
            strokeWidth,
            color,
          }),
        ];
      }
    }

    LC.defineShape('DashedRectangle', {
      constructor: function (args) {
        this.x = args.x;
        this.y = args.y;
        this.width = args.width;
        this.height = args.height;
        this.strokeColor = args.strokeColor;
        this.strokeWidth = args.strokeWidth;
        this.dashPattern = args.dashPattern || [5, 5];
      },

      // Bounding rectangle for the dashed rectangle
      getBoundingRect: function () {
        return {
          x: this.x,
          y: this.y,
          width: this.width,
          height: this.height,
        };
      },

      // Render the dashed rectangle on the canvas
      draw: function (ctx) {
        ctx.save();
        ctx.setLineDash(this.dashPattern); // Set dash pattern
        ctx.strokeStyle = this.strokeColor;
        ctx.lineWidth = this.strokeWidth;
        ctx.strokeRect(this.x, this.y, this.width, this.height);
        ctx.restore();
      },

      // Convert the shape to JSON for saving
      toJSON: function () {
        return {
          x: this.x,
          y: this.y,
          width: this.width,
          height: this.height,
          strokeColor: this.strokeColor,
          strokeWidth: this.strokeWidth,
          dashPattern: this.dashPattern,
        };
      },

      // Recreate the shape from JSON
      fromJSON: function (data) {
        return LC.createShape('DashedRectangle', data);
      },
    });


    class RectangleWithDotted extends LC.tools.Tool {
      name: string;
      iconName: string;
      optionsStyle: null;
      startX: number | null = null;
      startY: number | null = null;
      shape: any = null;

      constructor(lc: any) {
        super(lc);
        this.name = 'RectangleWithDotted';
        this.iconName = 'rectangle-dot'; // Use a custom icon if needed
        this.optionsStyle = null;
      }
      begin(x, y, lc) {
        this.startX = x;
        this.startY = y;

        // Create a placeholder rectangle shape
        this.shape = LC.createShape('Rectangle', {
          x: x,
          y: y,
          width: 0,
          height: 0,
          strokeWidth: lc.opts.defaultStrokeWidth,
          strokeColor: lc.getColor('primary'),
          fillColor: null,
          isClosed: true,
        });
      }

      continue(x, y, lc) {
        if (this.startX === null || this.startY === null) return;

        const width = x - this.startX;
        const height = y - this.startY;

        // Update placeholder rectangle dimensions
        this.shape.width = width;
        this.shape.height = height;

        // Clear and redraw in-progress shapes
        lc.repaintLayer('main');
        lc.drawShapeInProgress(this.shape);
      }

      end(x, y, lc) {
        if (this.startX === null || this.startY === null || !this.shape) return;

        const width = x - this.startX;
        const height = y - this.startY;

        // Create a dashed rectangle
        const dashedRectangle = LC.createShape('DashedRectangle', {
          x: this.startX,
          y: this.startY,
          width: width,
          height: height,
          strokeColor: lc.getColor('primary'),
          strokeWidth: lc.opts.defaultStrokeWidth,
          dashPattern: [5, 5], // Customize dash pattern
        });

        // Save the dashed rectangle to the canvas
        lc.saveShape(dashedRectangle, true);

        // Reset tool state
        this.startX = null;
        this.startY = null;
        this.shape = null;
      }
    }

    class ImageTool extends LC.tools.Tool {
      name: string;
      iconName: string;
      optionsStyle: string;
      images: string[];
      selectedImage: string | null;

      constructor(lc: any) {
        super(lc);
        this.name = 'ImageTool';
        this.iconName = 'images'; // Tool icon name, used in toolbar
        this.optionsStyle = 'lc-options';
      }
    }

    class StairsTool extends LC.tools.Tool {
      name: string;
      iconName: string;
      optionsStyle: null;
      startX: number | null = null;
      startY: number | null = null;
      shape: any = null;

      constructor(lc: any) {
        super(lc);
        this.name = 'Stairs';
        this.iconName = 'rectangle-stairs';
        this.optionsStyle = null;
      }

      begin(x: number, y: number, lc: any) {
        this.startX = x;
        this.startY = y;

        this.shape = LC.createShape('Rectangle', {
          x: x,
          y: y,
          width: 0,
          height: 0,
          strokeWidth: lc.opts.defaultStrokeWidth,
          strokeColor: lc.getColor('primary'),
          fillColor: null,
          isClosed: true,
        });
      }

      continue(x: number, y: number, lc: any) {
        if (this.startX === null || this.startY === null) return;

        this.shape.width = x - this.startX;
        this.shape.height = y - this.startY;

        lc.repaintLayer('main');
        lc.drawShapeInProgress(this.shape);
      }

      end(x: number, y: number, lc: any) {
        if (this.startX === null || this.startY === null || !this.shape) return;

        const x1 = Math.min(this.startX, x);
        const y1 = Math.min(this.startY, y);
        const x2 = Math.max(this.startX, x);
        const y2 = Math.max(this.startY, y);
        const width = Math.abs(x2 - x1);
        const height = Math.abs(y2 - y1);

        if (width < 1 || height < 1) {
          this.startX = null;
          this.startY = null;
          this.shape = null;
          return;
        }

        const strokeWidth = lc.opts.defaultStrokeWidth;
        const color = lc.getColor('primary');
        const stepInterval = componentRef.stairsStepInterval > 0 ? componentRef.stairsStepInterval : 20;

        // Outer rectangle stroke
        const rect = LC.createShape('Rectangle', {
          x: x1,
          y: y1,
          width: width,
          height: height,
          strokeWidth: strokeWidth,
          strokeColor: color,
          fillColor: null,
          isClosed: true,
        });
        lc.saveShape(rect, true);

        // Auto-detect orientation: lines perpendicular to the longest edge.
        // height >= width  -> vertical stairs (horizontal lines)
        // width > height   -> horizontal stairs (vertical lines)
        let horizontalLines = height >= width;
        if (componentRef.wideStairs) {
          horizontalLines = !horizontalLines;
        }

        if (horizontalLines) {
          const numSteps = Math.floor(height / stepInterval);
          for (let i = 1; i < numSteps; i++) {
            const ty = y1 + i * stepInterval;
            const line = LC.createShape('Line', {
              x1: x1,
              y1: ty,
              x2: x2,
              y2: ty,
              strokeWidth: strokeWidth,
              color: color,
            });
            lc.saveShape(line, true);
          }
        } else {
          const numSteps = Math.floor(width / stepInterval);
          for (let i = 1; i < numSteps; i++) {
            const tx = x1 + i * stepInterval;
            const line = LC.createShape('Line', {
              x1: tx,
              y1: y1,
              x2: tx,
              y2: y2,
              strokeWidth: strokeWidth,
              color: color,
            });
            lc.saveShape(line, true);
          }
        }

        this.startX = null;
        this.startY = null;
        this.shape = null;
      }
    }


    this.lc = LC.init(document.getElementsByClassName('literally2')[0], {
      imageURLPrefix: 'assets/literallycanvas/img',
      strokeWidths: [2, 5, 10, 25],
      primaryColor: '#000',
      tools: [
        LC.tools.Pencil,
        LC.tools.Eraser,
        LC.tools.Line,
        LC.tools.Rectangle,
        LC.tools.Ellipse,
        LC.tools.Text,
        LC.tools.Polygon,
        LC.tools.Pan,
        RectangleWithXTool,
        ImageTool,
        RectangleWithDotted,
        StairsTool
      ],
      secondaryColor: 'transparent',
      backgroundColor: 'transparent',
      defaultStrokeWidth: 2
    });
    
 
    // Intercept setTool to save text tool state when switching away
    const originalSetTool = this.lc.setTool.bind(this.lc);
    this.lc.setTool = function(tool: any) {
      // Save text tool state before switching away
      if (componentRef.lc.tool && componentRef.lc.tool.name === 'Text') {
        const fontString = componentRef.lc.tool.font || '16px sans-serif';

        // Extract italic, bold, fontSize, and fontFamily from font string
        const italic = /\bitalic\b/.test(fontString);
        const bold = /\bbold\b/.test(fontString);
        const cleanedString = fontString.replace(/^(italic|bold)(\s+(italic|bold))?\s+/, '');
        const fontMatch = cleanedString.match(/^(\d+)px\s+(.+)$/);

        let fontSize = 16;
        let fullFontFamily = 'sans-serif';
        let primaryFont = 'sans-serif';

        if (fontMatch) {
          fontSize = parseInt(fontMatch[1]);
          fullFontFamily = fontMatch[2].replace(/['"]/g, '');
          // Extract primary font (first in the list)
          primaryFont = fullFontFamily.split(',')[0].trim();
        }

        const state = {
          font: fontString,
          fontSize: fontSize,
          fontFamily: primaryFont,
          bold: bold,
          italic: italic
        };
        localStorage.setItem('textToolState_' + componentRef.job_id, JSON.stringify(state));
        componentRef.textToolState = state;
      }
      return originalSetTool(tool);
    };

    if (reportData.report_diagram_vector) {
      const snapshot = JSON.parse(reportData.report_diagram_vector);
      snapshot.shapes = snapshot.shapes.map((shape: any) => {
        if (shape.className === 'Image' && shape.data.imageSrc) {
          const img = new Image();
          img.src = shape.data.imageSrc;

          // Load the image and update its object
          img.onload = () => {
            shape.data.imageObject = img;
            try {
              this.lc.repaintAllLayers(); // Redraw after the image loads
            } catch (error) {
              console.error('Error during canvas redraw:', error);
            }
          };

          img.onerror = (error) => {
            console.error('Image failed to load:', shape.data.imageSrc, error);
          };

          shape.data.imageObject = img; // Assign the imageObject to the shape
        }
        return shape;
      });
      this.lc.loadSnapshot(snapshot);
      this.setSelectShapeTool();
    } else if (reportData.report_diagram_file) {
      if (reportData.report_diagram_file.startsWith('external_')) {
        this.external = true;
        this.loadExternalDiagram(reportData.report_diagram_file);
      } else {
        const newImage = new Image();
        newImage.src = this.diagramService.getDiagramUrl(this.job_id, reportData.report_diagram_file);
        this.lc.saveShape(LC.createShape('Image', {x: 10, y: 10, image: newImage}));
      }
      this.setSelectShapeTool();
    }


  }

  /** 1-11 in 1-11 mode, where the Type number is a location; otherwise 1-5. */
  private typeNumbersFor(cfg: any): number[] {
    const count = (cfg && cfg.is_eleven_mode) ? 11 : 5;
    const numbers = [];
    for (let n = 1; n <= count; n++) { numbers.push(n); }
    return numbers;
  }

  loadCustomText() {
    let optionString = '<option value=""></option>';
    let lettersString = '<option value=""></option>';
    let iconString = '<option value=""></option>';
    const letters = [
      'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I',
      'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R',
      'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'
    ];

    const arrayLength = this.findingsData.length;
    for (let i = 0; i < arrayLength; i++) {
      const strDropDown = this.findingsData[i].finding_type + this.findingsData[i].finding_finding;
      optionString += '<option value="' + strDropDown + '">' + strDropDown + '</option>';
    }


    // add a to z letters
    for (let j = 0; j < letters.length; j++) {
      lettersString += '<option value="' + letters[j] + '">' + letters[j] + '</option>';
    }
    // One quick-insert button per Type number. 1-5 or 1-11 depending on the deployment's
    // finding type mode, so the label typed onto the diagram matches the numbering the
    // findings use - see typeNumbers.
    const numberButtons = this.typeNumbers.map(n => `
      <button 
        class="letterPrefix btn btn-primary" 
        style="margin-left: 5px; padding: 0px 5px; font-size: 14px; background-color: #e9e9ed; border-color: #8f8f9d; color: #000; margin-top: -3px;" 
        data-value="${n}"
      >
        ${n}
      </button>`).join('');

    // tslint:disable-next-line:max-line-length
    optionString = `<style>
      .active-letter-prefix {
        background-color: #a6a6a6 !important;
      }
    </style>
    <div 
      id="textoptiondiv" 
      style="display: block; height: 30px; line-height: 31px; background-color: #f5f5f5; margin-left: 362px;margin-top: -30px;"
    >
      ${numberButtons}
      <select 
        id="lettersoptionselect" 
        style="margin-left: 5px;"
      >
        ${lettersString}
      </select>
      <button 
        id="selectNextLetter" 
        class="btn btn-primary" 
        style="margin-left: 5px; padding: 0px 5px; font-size: 14px; background-color: #e9e9ed; border-color: #8f8f9d; color: #000; margin-top: -3px;"
        >
        >
      </button>
    </div>
    <div id="imgOptions"
      style="display: block; height: 30px; line-height: 31px; background-color: #f5f5f5;"
      >
      <button 
        class="imgPicker btn btn-primary" 
        style="margin-left: 5px; padding: 0px 5px; font-size: 14px; background-color: #e9e9ed; border-color: #8f8f9d; color: #000; margin-top: -3px; " 
        data-value="assets/images/mouse.png"
      >
        <img style="width : 24px;" src="assets/images/mouse.png">
      </button>
      <button 
        class="imgPicker btn btn-primary" 
        style="margin-left: 5px; padding: 0px 5px; font-size: 14px; background-color: #e9e9ed; border-color: #8f8f9d; color: #000; margin-top: -3px;" 
        data-value="assets/images/bat.png"
      >
        <img style="width : 24px;" src="assets/images/bat.png">
      </button>
      <button 
        class="imgPicker btn btn-primary" 
        style="margin-left: 5px; padding: 0px 5px; font-size: 14px; background-color: #e9e9ed; border-color: #8f8f9d; color: #000; margin-top: -3px;" 
        data-value="assets/images/ant.png"
      >
        <img style="width : 24px;" src="assets/images/ant.png">
      </button>
      <button
        class="imgPicker btn btn-primary"
        style="margin-left: 5px; padding: 0px 5px; font-size: 14px; background-color: #e9e9ed; border-color: #8f8f9d; color: #000; margin-top: -3px;"
        data-value="assets/images/tree.png"
      >
        <img style="width : 18px;" src="assets/images/tree.png">
      </button>
      </div>
    <div id="stairsOptions"
      style="display: none; height: 30px; line-height: 31px; background-color: #f5f5f5; padding: 0 8px;"
      >
      <label style="font-size: 13px; margin-top: -3px; line-height: 30px; cursor: pointer;">
        <input type="checkbox" id="wideStairsCheckbox" style="vertical-align: middle; margin-right: 5px;">
        Wide Stairs
      </label>
    </div>
  `;


    $('.literally.toolbar-at-top .lc-options').append(optionString);

    $('.literally.toolbar-at-top .lc-options').find('.lc-font-settings').remove();

    if (this.lc.tool.name !== 'Text') {
      $('#textoptiondiv').hide();
    }
    if (this.lc.tool.name !== 'ImageTool') {
      $('#imgOptions').hide();
    }
    if (this.lc.tool.name !== 'Stairs') {
      $('#stairsOptions').hide();
    }

    const stairsHost = this;
    $('#wideStairsCheckbox').prop('checked', this.wideStairs).on('change', function () {
      stairsHost.wideStairs = $(this).is(':checked');
    });

    // LiterallyCanvas font sizes: these map to indices in the font size dropdown
    const LC_FONT_SIZES = [9, 10, 12, 14, 18, 24, 36, 48, 64, 72, 96, 144, 288];

    // Parse font string to extract italic, bold, fontSize, and fontFamily
    const parseFontString = (fontString: string) => {
      // Font format examples:
      // "18px Helvetica"
      // "italic 18px Helvetica"
      // "bold 18px Helvetica"
      // "italic bold 24px Helvetica"
      // "bold italic 24px Helvetica"
      // "italic bold 14px "Trebuchet MS","Lucida Grande","Lucida Sans Unicode","Lucida Sans",Tahoma,sans-serif"

      const italic = /\bitalic\b/.test(fontString);
      const bold = /\bbold\b/.test(fontString);

      // Remove italic and bold prefixes from the beginning
      const cleanedString = fontString.replace(/^(italic|bold)(\s+(italic|bold))?\s+/, '');

      // Match the font size and family
      const fontMatch = cleanedString.match(/^(\d+)px\s+(.+)$/);

      if (fontMatch) {
        const fullFontFamily = fontMatch[2].replace(/['"]/g, '');
        // Extract just the primary font name (first in the comma-separated list)
        const primaryFont = fullFontFamily.split(',')[0].trim();

        return {
          fontSize: parseInt(fontMatch[1]),
          fontFamily: primaryFont,
          fullFontFamily: fullFontFamily,
          italic: italic,
          bold: bold
        };
      }

      return {
        fontSize: 16,
        fontFamily: 'sans-serif',
        fullFontFamily: 'sans-serif',
        italic: false,
        bold: false
      };
    };

    // Convert pixel font size to dropdown index
    const fontSizeToIndex = (fontSize: number): number => {
      const index = LC_FONT_SIZES.indexOf(fontSize);
      return index >= 0 ? index : 5; // Default to index 5 (24px) if not found
    };

    // Save text tool state whenever toolbar inputs change
    let lastSavedState: any = null;
    let skipSaveUntil = 0; // Timestamp to skip saves until
    const saveTextToolState = () => {
      if (that.tool && that.tool.name === 'Text') {
        // Skip saves for a brief moment after restoration
        if (Date.now() < skipSaveUntil) {
          return;
        }

        const parsed = parseFontString(that.tool.font || '16px sans-serif');
        const state = {
          font: that.tool.font || '16px sans-serif',
          fontSize: parsed.fontSize,
          fontFamily: parsed.fontFamily,
          bold: parsed.bold,
          italic: parsed.italic
        };

        // IMPORTANT: After restoration, if bold/italic were present in lastSavedState
        // but are now missing, preserve them (prevents incomplete rebuilds from overwriting)
        if (lastSavedState && (lastSavedState.bold || lastSavedState.italic)) {
          // If current state has lost bold/italic but lastSavedState had them,
          // this suggests the change event rebuilt the font incompletely
          // Preserve the last saved bold/italic unless explicitly changed
          if (!state.bold && lastSavedState.bold) {
            state.bold = lastSavedState.bold;
          }
          if (!state.italic && lastSavedState.italic) {
            state.italic = lastSavedState.italic;
          }

          // Rebuild font string with preserved properties
          const stylePrefix = (state.bold && state.italic) ? 'italic bold ' : state.italic ? 'italic ' : state.bold ? 'bold ' : '';
          state.font = stylePrefix + state.fontSize + 'px ' + parsed.fontFamily;

          console.log('Preserved bold/italic from last save:', { bold: state.bold, italic: state.italic });
        }

        // Only save if state has actually changed
        if (lastSavedState && JSON.stringify(state) === JSON.stringify(lastSavedState)) {
          return;
        }

        lastSavedState = state;
        localStorage.setItem('textToolState_' + thisthat.job_id, JSON.stringify(state));
        thisthat.textToolState = state;
        console.log('Text tool state saved:', state);
      }
    };

    // Listen for changes in font size, bold, italic controls
    $('.literally.toolbar-at-top').on('change', 'input[type="range"], input[type="checkbox"], select', function() {
      saveTextToolState();
    });

    // Also save state periodically while text tool is active (every 1000ms)
    let textToolStateCheckInterval: any;
    const startStateMonitoring = () => {
      if (!textToolStateCheckInterval) {
        textToolStateCheckInterval = setInterval(() => {
          if (that.tool && that.tool.name === 'Text') {
            saveTextToolState();
          }
        }, 1000);
      }
    };

    const stopStateMonitoring = () => {
      if (textToolStateCheckInterval) {
        clearInterval(textToolStateCheckInterval);
        textToolStateCheckInterval = null;
      }
    };

    const that = this.lc;
    const thisthat = this;

    const unsubscribe = this.lc.on('toolChange', function ({tool}) {
      console.log(tool.name);
      if (tool.name === 'Stairs') {
        $('#stairsOptions').show();
        $('#imgOptions').hide();
        $('#textoptiondiv').hide();
        $('#wideStairsCheckbox').prop('checked', thisthat.wideStairs);
        return;
      } else {
        $('#stairsOptions').hide();
      }
      if (tool.name === 'Text') {
        $('#imgOptions').hide();
        $('#textoptiondiv').show();
        $('#textoptiondiv').css('margin-top',"-30px");
        $('#textoptiondiv').css('margin-left',"362px");

        // Check if we have a saved state FIRST
        const savedState = localStorage.getItem('textToolState_' + thisthat.job_id);
        let restoredState: any = null;
        if (savedState) {
          try {
            restoredState = JSON.parse(savedState);
            thisthat.textToolState = restoredState;

            // Defer restoration until after the tool initializes
            setTimeout(() => {
              console.log('Tool before deferred restoration:', { font: tool.font, bold: tool.bold, italic: tool.italic });

              // Restore font string (which contains all formatting: italic, bold, fontSize, fontFamily)
              // This is the COMPLETE source of truth - don't let change events overwrite it
              if (restoredState.font) {
                tool.font = restoredState.font;
                console.log('Restored font to:', tool.font);
              }

              // Update toolbar UI values WITHOUT triggering change events
              // The tool.font is already correct; UI is just for display
              const toolbar = $('.literally.toolbar-at-top');

              // Set checkbox values (visual feedback only, no change events)
              const boldCheckbox = toolbar.find('#bold');
              if (boldCheckbox.length) {
                boldCheckbox.prop('checked', restoredState.bold);
                console.log('Updated bold checkbox display to:', restoredState.bold);
              }

              const italicCheckbox = toolbar.find('#italic');
              if (italicCheckbox.length) {
                italicCheckbox.prop('checked', restoredState.italic);
                console.log('Updated italic checkbox display to:', restoredState.italic);
              }

              // Set select values (visual feedback only, no change events)
              const fontSizeIndex = fontSizeToIndex(restoredState.fontSize);
              const fontSizeSelect = toolbar.find('select:first');
              if (fontSizeSelect.length) {
                fontSizeSelect.val(fontSizeIndex);
                console.log('Updated font size select display to index:', fontSizeIndex);
              }

              const fontFamilySelect = toolbar.find('select').eq(1);
              if (fontFamilySelect.length) {
                let found = false;
                fontFamilySelect.find('option').each(function() {
                  const optionText = $(this).text().trim();
                  if (optionText === restoredState.fontFamily) {
                    fontFamilySelect.val($(this).val());
                    found = true;
                    return false;
                  }
                });
                if (found) {
                  console.log('Updated font family select display to:', restoredState.fontFamily);
                } else {
                  console.log('Font family not found in dropdown:', restoredState.fontFamily);
                }
              }

              console.log('Tool after restoration (UI updated):', { font: tool.font });

              // Set skip protection to block the NEXT change events
              // This prevents user interactions from saving while React state is settling
              // Set to a longer duration since React batches updates and can take time
              skipSaveUntil = Date.now() + 2500;
              lastSavedState = restoredState;

              console.log('Text tool state fully restored from storage:', restoredState);
            }, 50); // 50ms delay to let the tool initialize
          } catch (error) {
            console.error('Error restoring text tool state:', error);
          }
        } else {
          // Only call setText if there's NO saved state (first time or after clear)
          const selectedValue = $('#textoptionselect').val();
          if (selectedValue === '') {
            tool.dropdownText = false;
          } else {
            tool.dropdownText = true;
          }
          tool.setText(selectedValue);
        }

        $('.imgPicker').removeClass('active-letter-prefix');
        $('.letterPrefix').show();
        $('#selectNextLetter').show();
        $('#lettersoptionselect').show();

        // Start monitoring for text tool state changes
        startStateMonitoring();
      } else if ( tool.name === 'ImageTool'){
        // Stop monitoring and save text tool state before switching away
        stopStateMonitoring();
        if (that.tool && that.tool.name === 'Text') {
          const parsed = parseFontString(that.tool.font || '16px sans-serif');
          const state = {
            font: that.tool.font || '16px sans-serif',
            fontSize: parsed.fontSize,
            fontFamily: parsed.fontFamily,
            bold: parsed.bold,
            italic: parsed.italic
          };
          localStorage.setItem('textToolState_' + thisthat.job_id, JSON.stringify(state));
          thisthat.textToolState = state;
        }

        $('#imgOptions').show();
        $('#textoptiondiv').hide();
      } else if ( tool.name === 'SelectShape'){
        // Stop monitoring and save text tool state before switching away
        stopStateMonitoring();
        if (that.tool && that.tool.name === 'Text') {
          const parsed = parseFontString(that.tool.font || '16px sans-serif');
          const state = {
            font: that.tool.font || '16px sans-serif',
            fontSize: parsed.fontSize,
            fontFamily: parsed.fontFamily,
            bold: parsed.bold,
            italic: parsed.italic
          };
          localStorage.setItem('textToolState_' + thisthat.job_id, JSON.stringify(state));
          thisthat.textToolState = state;
        }

        $('#imgOptions').show();
        $('#textoptiondiv').hide();
      } else {
        // Stop monitoring and save text tool state before switching away
        stopStateMonitoring();
        if (that.tool && that.tool.name === 'Text') {
          const parsed = parseFontString(that.tool.font || '16px sans-serif');
          const state = {
            font: that.tool.font || '16px sans-serif',
            fontSize: parsed.fontSize,
            fontFamily: parsed.fontFamily,
            bold: parsed.bold,
            italic: parsed.italic
          };
          localStorage.setItem('textToolState_' + thisthat.job_id, JSON.stringify(state));
          thisthat.textToolState = state;
        }

        $('#textoptiondiv').hide();
        $('#imgOptions').hide();
      }


    });

    $('#textoptionselect').on('change', function () {
      if (that.tool.name === 'Text') {
        $('.letterPrefix').removeClass('active-letter-prefix');
        thisthat.letterPrefix = '';
        const selectedValue = $('#textoptionselect').val();
        if (selectedValue === '') {
          that.tool.dropdownText = false;
        } else {
          that.tool.dropdownText = true;
        }
        that.tool.setText(selectedValue);
      }
    });
    $('.letterPrefix').on('click', function () {
      $('.letterPrefix').removeClass('active-letter-prefix');
      $(this).addClass('active-letter-prefix');
      thisthat.letterPrefix = $(this).attr('data-value');
      thisthat.setCustomText();
    });
    $('#lettersoptionselect').on('change', function () {
      thisthat.setCustomText();
    });

    $('#selectNextLetter').on('click', function () {
      const optionSelected = $('#lettersoptionselect > option:selected');
      optionSelected.removeAttr('selected');
      optionSelected.next('option').attr('selected', 'selected');
      thisthat.setCustomText();
    });
    $('.imgPicker').on('click', function () {
      $('.imgPicker').removeClass('active-letter-prefix');
      $(this).addClass('active-letter-prefix');
      const value = $(this).attr('data-value');
      thisthat.setCustomImage(value);
     });
  }
  setCustomImage(value) {
    const that = this.lc;
    const thisthat = this;
    const img = new Image();
    img.src = value; // Assuming selectedValue is the image URL

    img.onload = function () {
      // Add the image to the canvas
      const imgShape = LC.createShape('Image', {
        x: 100, // X position of the image
        y: 100, // Y position of the image
        width: img.width, // Image width
        height: img.height, // Image height
        image: img,
      });

      // Add the shape to the canvas
      that.saveShape(imgShape);
      thisthat.setSelectShapeTool();
    };
  }
  setSelectShapeTool(): void {
    const selectShapeTool = new LC.tools.SelectShape(this.lc);
    this.lc.setTool(selectShapeTool);
    // console.log(this.lc.getSnapshot())
  }

  setCustomText() {
    const that = this.lc;
    const thisthat = this;
    if (that.tool.name === 'Text') {
      let selectedValue = $('#lettersoptionselect').val();
      if (selectedValue === '') {
        that.tool.dropdownText = false;
      } else {
        that.tool.dropdownText = true;
      }
      selectedValue = thisthat.letterPrefix + selectedValue;
      that.tool.setText(selectedValue);
    }
  }
  openExternalEditor(){
    localStorage.setItem('vector_'+this.job_id, JSON.stringify(this.lc.getSnapshot()));
    const baseHref = this.location.prepareExternalUrl('');
    const url = this._router.serializeUrl(
      this._router.createUrlTree(['rodents', this.job_id])
    );
    window.open(`${baseHref}${url}`, '_blank');
  }
  upload_diagram(lc, typeSave) {
    const snapshot = JSON.stringify(lc.getSnapshot());
    if(typeSave == 'final'){
      this.draw_border(lc);
    }
    const image = lc.getImage().toDataURL();
    const type = 'base64';
    this.uploadImage = {
      'snapshot': snapshot,
      'image': image,
      'type': type,
      'report': this.reportData
    };

    this.diagramService.uploadDiagram(this.uploadImage).subscribe(
      data => {
        this.saveSuccess = true;
        this.typeSave = typeSave;
        // Use server timestamp if available, otherwise use client time
        const response = data as any;
        if (response && response.report_diagram_last_saved_at) {
          this.formatAndSetLastSavedTime(response.report_diagram_last_saved_at);
        } else {
          this.updateLastSavedTime();
        }
        setTimeout(() => {
          this.saveSuccess = false;
        }, 2000);
        // Swal(
        //   'Saved!',
        //   'Diagram has been saved.',
        //   'success'
        // );
      }
    );
  }

  updateLastSavedTime() {
    this.lastSavedTimestamp = Date.now();
    const now = new Date();
    const date = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const time = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
    this.lastSavedTime = `${date} at ${time}`;
  }

  formatAndSetLastSavedTime(serverTimestamp: string) {
    const date = new Date(serverTimestamp);
    const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const timeStr = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
    this.lastSavedTime = `${dateStr} at ${timeStr}`;
    this.lastSavedTimestamp = date.getTime();
  }

  doneReview() {
    this._router.navigate(['inspections-profile-review/', this.job_id]);
    // window.location.href = this.apiSite+'url/inspections_profile_review/'+this.job_id;
  }

  draw_border(lc) {
    // console.log(shapes);
    const shapes = lc.shapes;
    const len = shapes.length;
    if (len < 2) {
      return;
    }
    let beforeRect;
    beforeRect = shapes[0].getBoundingRect();

    let xMin, xMax, yMin, yMax, width, height;
    xMin = xMax = beforeRect.x;
    yMin = yMax = beforeRect.y;
    for (let i = 0; i < len; i++) {
      const rect = shapes[i].getBoundingRect();
      let x = rect.x;
      let y = rect.y;

      if (x < xMin) {
        xMin = x;
      }
      if (x > xMax) {
        xMax = x;
      }
      if (y < yMin) {
        yMin = y;
      }
      if (y > yMax) {
        yMax = y;
      }
      x = rect.x + rect.width;
      y = rect.y + rect.height;

      if (x < xMin) {
        xMin = x;
      }
      if (x > xMax) {
        xMax = x;
      }
      if (y < yMin) {
        yMin = y;
      }
      if (y > yMax) {
        yMax = y;
      }

    }
    width = xMax - xMin;
    height = yMax - yMin;
    // alert("here");
    // createShape('Rectangle', {x, y, width, height, strokeWidth, strokeColor, fillColor})
    if (
      xMin !== beforeRect.x
      || yMin !== beforeRect.y
      || width !== beforeRect.width
      || height !== beforeRect.height
    ) {
      xMin -= 5;
      yMin -= 5;
      width += 10;
      height += 10;
    }

    lc.saveShape(LC.createShape('Rectangle', {
      x: xMin,
      y: yMin,
      width: width,
      height: height,
      stokeWidth: 1,
      strokeColor: 'white',
      fillColor: 'transparent'
    }));
  }

  saveDiagram(type) {
    if (this.lc.tool.name === 'Text') {

      if (this.lc.tool.currentShapeState === 'selected' || this.lc.tool.currentShapeState === 'editing') {

        Swal({
          title: 'Important information',
          text: 'There is not committed text in the diagram. Are you sure you want to save this text into the diagram?',
          type: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: 'Yes'
        })
          .then((result) => {
            if (result.value) {
              this.upload_diagram(this.lc, type);
            }
          });

      } else {
        this.upload_diagram(this.lc, type);
      }
    } else {
      this.upload_diagram(this.lc, type);
    }
  }

  clearCacheAdm() {
    this.diagramService.clearCache(this.job_id).subscribe(
      data => {
        this.ngOnInit();
      }
    );
  }

  loadExternalDiagram(filename) {
    const self = this;
    const xhr = new XMLHttpRequest();
    xhr.onload = function() {
      const reader = new FileReader();
      reader.onloadend = function() {
        self.loaded = true;
        self.imageLoaded = true;
        self.imageSrc = reader.result;
      };
      reader.readAsDataURL(xhr.response);
    };
    xhr.open('GET', this.diagramService.getDiagramUrl(this.job_id, filename));
    xhr.responseType = 'blob';
    xhr.send();
  }

  upload_external_diagram() {
    if (!this.imageSrc.startsWith('data:image')) {
      Swal(
        'No image found!',
        'Please select a JPG or PNG image.',
        'error'
      );
      return;
    }

    this.uploadImage = {
      'snapshot': null,
      'image': this.imageSrc,
      'type': 'base64',
      'report': this.reportData,
      'external': true,
    };

    this.diagramService.uploadDiagram(this.uploadImage).subscribe(
      data => {
        Swal(
          'Saved!',
          'External diagram has been saved.',
          'success'
        );
      },
      error => {
        Swal(
          'Oh no!',
          'Something went wrong while trying to upload the image.',
          'error'
        );
      }
    );
  }

  handleDragEnter() {
    this.dragging = true;
  }

  handleDragLeave() {
    this.dragging = false;
  }

  handleDrop(e) {
    e.preventDefault();
    this.dragging = false;
    this.handleInputChange(e);
  }

  handleImageLoad() {
    this.imageLoaded = true;
  }

  handleInputChange(e) {
    const file = e.dataTransfer ? e.dataTransfer.files[0] : e.target.files[0];

    const pattern = /image-*/;
    const reader = new FileReader();

    if (!file.type.match(pattern)) {
      Swal(
        'Invalid Format!',
        'Please select a JPG or PNG image.',
        'error'
      );
      return;
    }

    this.loaded = false;

    reader.onload = this._handleReaderLoaded.bind(this);
    reader.readAsDataURL(file);
  }

  _handleReaderLoaded(e) {
    const reader = e.target;
    this.imageSrc = reader.result;
    this.loaded = true;
  }
}
